import { z } from 'zod'

import { TicketIdSchema } from '../../models/ticket/ticket.dto'
import type { TicketEventSchema } from '../../models/ticketEvent/ticketEvent.dto'
import { TicketMessageSchema } from '../../models/ticketMessage/ticketMessage.dto'
import { TicketMessageIdSchema } from '../../models/ticketMessage/ticketMessageId'
import type { TicketActionResultForQA } from '../ticket/ticket'

// Emitted when a new TicketEvent is created for the ticket
export const NewEventPayloadSchema = z.object({
  type: z.literal('new-event'),
  ticketId: TicketIdSchema,
  event: z.custom<
    z.infer<typeof TicketEventSchema> & {
      createdAt: string
      remoteCreatedAt?: string | null
    }
  >(),
})
export type NewEventPayload = z.infer<typeof NewEventPayloadSchema>

// Emitted when a new TicketMessage is created for the ticket even if it's blank.
// Follow up MessageChunkPayloads will be emitted when the message is streamed in.
export const NewMessagePayloadSchema = z.object({
  type: z.literal('new-message'),
  ticketId: TicketIdSchema,
  // The content might be empty, but the message is still created.
  message: TicketMessageSchema.extend({
    createdAt: z.string().datetime(),
    remoteCreatedAt: z.string().datetime().nullable(),
  }),
})
export type NewMessagePayload = z.infer<typeof NewMessagePayloadSchema>

// Emitted when a new chunk of a message is streamed in.
export const MessageChunkPayloadSchema = z.object({
  type: z.literal('message-chunk'),
  ticketId: TicketIdSchema,
  messageId: TicketMessageIdSchema,
  contentDelta: z.string(),
  // Use this field to sort chunks (ASC) if events arrive out of order.
  sortOrder: z.number(),
})
export type MessageChunkPayload = z.infer<typeof MessageChunkPayloadSchema>

// Emitted when a message is complete, no more chunks will be emitted.
export const MessageCompletePayloadSchema = z.object({
  type: z.literal('message-complete'),
  ticketId: TicketIdSchema,
  messageId: TicketMessageIdSchema,
})
export type MessageCompletePayload = z.infer<
  typeof MessageCompletePayloadSchema
>

// Emitted on actions update
export const ActionExecutedPayloadSchema = z.object({
  type: z.literal('action-executed'),
  ticketId: TicketIdSchema,
  executionResult: z.enum(['success', 'error']),
  result: z.custom<TicketActionResultForQA>(),
})
export type ActionExecutedPayload = z.infer<typeof ActionExecutedPayloadSchema>

export const TicketChannelPayloadSchema = z.discriminatedUnion('type', [
  NewEventPayloadSchema,
  NewMessagePayloadSchema,
  MessageChunkPayloadSchema,
  MessageCompletePayloadSchema,
  ActionExecutedPayloadSchema,
])
export type TicketChannelPayload = z.infer<typeof TicketChannelPayloadSchema>
